import {toJS} from 'mobx'
import {logger, ObjUtil, Time, Util} from 'yougile-util'
import {
  Chat,
  createSubtaskFromTask,
  DataObjectId,
  Hub,
  noBoardContainerId,
  SandboxTree,
  SubtaskList,
  Tag
} from 'yougile-model'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Sync} from '../client/sync'
import {Board} from './board'
import {Column} from './column'
import {Project} from './project'
import {ColumnData, ProjectData, TaskLocalData} from './project-import-to-json'
import {Sticker} from './sticker/sticker'
import {Task} from './task'

export const TASKS_BATCH_SIZE = 100
export const TASKS_BATCH_DELAY = 1000

const PROGRESS_COLUMNS_DONE = 1
const PROGRESS_TASKS_DONE = 80
const PROGRESS_SUBTASKS_DONE = 98

export const projectImportFromJson = async ({
  data,
  setProgress,
  isActive
}: {
  data: ProjectData
  setProgress?: (progress: number) => void
  isActive?: () => boolean
}): Promise<void> => {
  const columnTaskIds: {[taskId: string]: DataObjectId} = {}
  const columns: [Hub, ColumnData][] = []

  SyncCommitHandler.doInBatch(() => {
    const box = Sync.getCurrentSandbox()
    const project = Project.createNew(box.companyId, data.title, false, false)
    data.stickers.forEach(s => {
      if (!isActive?.()) {
        return
      }

      if (!box.has(s.id)) {
        const newSt = new Tag(box.companyId, '')
        newSt.id = s.id
        newSt.title = s.title
        s.states && (newSt.states = s.states)
        s.properties && (newSt.properties = s.properties)
        Sticker.add(box, newSt)
      }
    })

    if (!isActive?.()) {
      return
    }

    data.boards.forEach(b => {
      if (!isActive()) {
        return
      }

      const board = Project.addBoard(b.title, project, false, false, true)
      board.stickers = ObjUtil.filter(b.stickers, (id: DataObjectId) =>
        box.has(id)
      )
      board.commit()
      b.columns.forEach(c => {
        const column = Board.addColumn(c.title, board, false)
        column.description = c.color
        column.commit()

        columns.push([column, c])
        c.tasks.forEach(t => {
          columnTaskIds[t] = column.id
        })
      })
    })
  })

  setProgress?.(PROGRESS_COLUMNS_DONE)

  const taskMap: {[taskId: string]: Hub} = {}
  let tasks = Object.entries(data.tasks)

  // create tasks
  const taskProgressSize = PROGRESS_TASKS_DONE - PROGRESS_COLUMNS_DONE
  let tasksProcessed = 0
  const len = tasks.length
  while (tasks.length && isActive?.()) {
    const piece = tasks.splice(0, TASKS_BATCH_SIZE)
    await createTasks({data, tasks: piece, columnTaskIds, taskMap})
    tasks.length && (await Util.wait(TASKS_BATCH_DELAY))
    tasksProcessed += piece.length
    setProgress?.(
      PROGRESS_COLUMNS_DONE + taskProgressSize * (tasksProcessed / len)
    )
  }

  // create subtasks
  tasks = Object.entries(data.tasks)
  tasksProcessed = 0
  const subtaskProgressSize = PROGRESS_SUBTASKS_DONE - PROGRESS_TASKS_DONE
  while (tasks.length && isActive?.()) {
    const piece = tasks.splice(0, TASKS_BATCH_SIZE)
    await createSubtasks({data, tasks: piece, columnTaskIds, taskMap})
    tasks.length && (await Util.wait(TASKS_BATCH_DELAY))
    tasksProcessed += piece.length
    setProgress?.(
      PROGRESS_TASKS_DONE + subtaskProgressSize * (tasksProcessed / len)
    )
  }

  // sort tasks in columns
  columns.forEach(([column, data]) => {
    const order = Column.getTasksOrder(column)
    const tasksWithOrder = data.tasks.map(taskId => taskMap[taskId])
    order.setEntities(tasksWithOrder)
  })
  setProgress?.(100)
}

const createTasks = async ({
  data,
  tasks,
  columnTaskIds,
  taskMap
}: {
  data: ProjectData
  tasks: [string, TaskLocalData][]
  columnTaskIds: {[taskId: string]: DataObjectId}
  taskMap: {[taskId: string]: Hub}
}): Promise<void> => {
  const box = Sync.getCurrentSandbox()

  SyncCommitHandler.doInBatch(() => {
    tasks.forEach(([taskId]) => {
      const location = columnTaskIds[taskId] ?? noBoardContainerId

      if (typeof taskId !== 'string') {
        const error = 'Wrong json format, expected to have the list of task ids'
        logger.error(error)
        throw new Error(error)
      }

      taskMap[taskId] = createTask({
        box,
        task: data.tasks[taskId],
        location
      })
    })
  })
}

const createSubtasks = async ({
  tasks,
  taskMap
}: {
  data: ProjectData
  tasks: [string, TaskLocalData][]
  columnTaskIds: {[taskId: string]: DataObjectId}
  taskMap: {[taskId: string]: Hub}
}): Promise<void> => {
  SyncCommitHandler.doInBatch(() => {
    tasks.forEach(([taskId, taskJson]) => {
      const order = taskJson.subtasks.map(id => taskMap[id].id)
      const subtasks: SubtaskList['subtasks'] = {}
      taskJson.subtasks.forEach(id => {
        const hub = taskMap[id]
        const subtask = createSubtaskFromTask(hub)
        subtask && (subtasks[hub.id] = subtask)
      })

      const hub = taskMap[taskId]
      const list: SubtaskList = {subtasks, order}
      hub.data.subtaskList = toJS(list)
      hub.commit()
    })
  })
}

const createTask = ({
  box,
  task,
  location
}: {
  box: SandboxTree
  task: TaskLocalData
  location: DataObjectId
}): Hub => {
  const hub = Task.createTask(task.title, {
    location,
    noAutoSubscribe: true,
    toEnd: true
  })
  if (!hub) {
    throw new Error(`Failed to create task ${task.title}`)
  }
  hub.description = task.description
  hub.data = Util.clone(task.data ?? {})
  hub.complete = task.complete
  hub.timestamp = task.timestamp ?? Time.now()
  hub.chat = new Chat(box.companyId).fromJSON(box.companyId, task.chat)
  hub.stickers = ObjUtil.filter(task.stickers, (stickerId: DataObjectId) =>
    box.has(stickerId)
  )
  hub.commit()

  return hub
}
